import { useState, useEffect, useContext, useRef, useLayoutEffect } from "react";
import { useMsal } from "@azure/msal-react";
import styles from "./UserProfileMenu.module.css";
import { checkLoggedIn, getRedirectUri, loginRequest, useLogin, useMsalAuth } from "../../authConfig";
import { LoginContext } from "../../loginContext";
import { Building2, CircleUserRound, Languages, LogOut, Settings, ShieldCheck, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DefaultButton, Spinner } from "@fluentui/react";
import { createPortal } from "react-dom";
import { configApi } from "../../api";
import { supportedLngs } from "../../i18n/config";
import { UserSettings } from "../UserSettings";
import { useNavigate } from "react-router-dom";
import { signOut } from "../../api/signOut";
import { clearClientBrainCache } from "../../api/clientBrainApi";
import { useCompany } from "../../contexts/CompanyContext";
import { useOrganization } from "../../contexts/OrganizationContext";
import { calculateDropdownPosition, observeDropdownSize } from "./userProfileMenuPosition";

type UserProfileMenuProps = {
    sidebarOpen: boolean;
};

export const UserProfileMenu = ({ sidebarOpen }: UserProfileMenuProps) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    // Support both MSAL (old Azure AD) and custom OAuth (email/Google/Microsoft)
    const msalEnabled = useMsalAuth;
    const msalContext = msalEnabled ? useMsal() : null;
    const instance = msalContext?.instance;
    const { loggedIn, setLoggedIn, userInfo, isLoading } = useContext(LoginContext);
    const { company, companyRole, roleReady } = useCompany();
    const {
        context: organizationContext,
        currentMembership,
        loading: organizationLoading,
        canAccessAdministration,
        administrationRequiresOrgAdmin,
        switching,
        switchOrganization
    } = useOrganization();
    const activeAccount = instance?.getActiveAccount();

    const languageButtonRef = useRef<HTMLButtonElement>(null);
    const languageDropdownRef = useRef<HTMLDivElement>(null);

    const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
    const [userDisplayData, setUserDisplayData] = useState({ name: "", email: "", photoUrl: "" });
    const [menuVisible, setMenuVisible] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<React.CSSProperties>({});
    const [showLanguagePicker, setshowLanguagePicker] = useState<boolean>(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const explicitRoles = currentMembership?.explicitRoleAssignments ?? [];
    const compactRoleSummary = explicitRoles
        .slice(0, 2)
        .map(assignment => `${assignment.scopeName} · ${assignment.role}`)
        .join(", ");
    const additionalRoleCount = Math.max(0, explicitRoles.length - 2);
    const organizationSelectionRequired = organizationContext?.state === "selection_required";

    const buttonRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const getConfig = async () => {
        configApi().then(config => {
            setshowLanguagePicker(config.showLanguagePicker);
        });
    };

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (
                languageDropdownRef.current &&
                !languageDropdownRef.current.contains(e.target as Node) &&
                !languageButtonRef.current?.contains(e.target as Node)
            ) {
                setLanguageMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setUserDisplayData(userInfo);
    }, [userInfo]);

    useLayoutEffect(() => {
        if (!menuVisible || !buttonRef.current || !dropdownRef.current) {
            return;
        }

        const updateDropdownPosition = () => {
            if (!buttonRef.current || !dropdownRef.current) return;
            const buttonRect = buttonRef.current.getBoundingClientRect();
            const dropdownHeight = dropdownRef.current.offsetHeight;

            setDropdownPosition(calculateDropdownPosition(buttonRect, dropdownHeight, sidebarOpen));
        };

        const stopObserving = observeDropdownSize(dropdownRef.current, updateDropdownPosition, onResize => new ResizeObserver(onResize));
        window.addEventListener("resize", updateDropdownPosition);
        return () => {
            stopObserving();
            window.removeEventListener("resize", updateDropdownPosition);
        };
    }, [menuVisible, sidebarOpen]);

    useEffect(() => {
        getConfig();

        const cached = localStorage.getItem("userInfo");
        if (cached) {
            try {
                setUserDisplayData(JSON.parse(cached));
            } catch {
                localStorage.removeItem("userInfo");
                clearClientBrainCache();
            }
        }
    }, []);

    useEffect(() => {
        const onClickOutside = (event: MouseEvent) => {
            if (
                menuVisible &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                !buttonRef.current?.contains(event.target as Node)
            ) {
                setMenuVisible(false);
            }
        };

        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [menuVisible]);

    const handleLoginPopup = () => {
        if (useLogin && instance) {
            // MSAL login for old Azure AD users
            instance
                .loginPopup({
                    ...loginRequest,
                    redirectUri: getRedirectUri()
                })
                .catch(error => console.log(error))
                .then(async () => {
                    setLoggedIn(await checkLoggedIn(instance));
                });
        } else {
            // Custom OAuth login - redirect to login page
            navigate("/login");
        }
    };

    const handleLogoutPopup = async () => {
        try {
            await signOut(msalEnabled ? instance : undefined);
            setLoggedIn(false);
            setUserDisplayData({ name: "", email: "", photoUrl: "" });
            navigate("/login");
        } catch (error: unknown) {
            console.error("Logout failed:", error);
        }
    };

    return (
        <div className={styles.wrapper}>
            {loggedIn && (
                <div
                    className={`${styles.profileContainer} ${!sidebarOpen ? styles.contentCenter : ""} glass-interactive glass-focusable`}
                    ref={buttonRef}
                    onClick={() => setMenuVisible(v => !v)}
                    role="button"
                    tabIndex={0}
                    aria-haspopup="menu"
                    aria-expanded={menuVisible}
                    onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setMenuVisible(v => !v);
                        }
                    }}
                >
                    {userDisplayData.photoUrl ? (
                        <img src={userDisplayData.photoUrl} alt="User" className={`${styles.profilePhoto} ${!sidebarOpen ? styles.photoOnly : ""}`} />
                    ) : (
                        <User className={`${styles.profilePhotoPlaceholder} ${!sidebarOpen ? styles.photoOnly : ""}`} />
                    )}

                    {sidebarOpen && (
                        <div className={styles.userInfoContainer}>
                            <span className={styles.usernameText}>{userDisplayData.name}</span>
                            <span className={styles.emailText}>{userDisplayData.email}</span>
                            {currentMembership ? (
                                <span className={styles.organizationContext}>
                                    {currentMembership.organization.name}
                                    {compactRoleSummary ? ` · ${compactRoleSummary}` : ""}
                                    {additionalRoleCount ? ` · +${additionalRoleCount} more` : ""}
                                </span>
                            ) : organizationLoading ? (
                                <span className={styles.organizationContext}>Loading Organization access…</span>
                            ) : organizationSelectionRequired ? (
                                <span className={styles.organizationContext}>Select Organization · {organizationContext.memberships.length} available</span>
                            ) : import.meta.env.DEV && company ? (
                                <span className={styles.organizationContext}>
                                    {company.name} · {companyRole === "admin" ? "Administrator" : "Member"}
                                </span>
                            ) : null}
                        </div>
                    )}
                </div>
            )}

            {menuVisible &&
                loggedIn &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className={`${styles.dropdown} ${!sidebarOpen ? styles.fixedSizeDropdown : ""} glass-blur-fallback`}
                        style={dropdownPosition}
                        role="menu"
                    >
                        {organizationLoading && !organizationContext ? (
                            <div className={styles.organizationSection}>
                                <span className={styles.sectionLabel}>Organization access</span>
                                <div className={styles.organizationIdentity}>
                                    <Spinner />
                                    <span>
                                        <strong>Loading Organization…</strong>
                                        <small>Resolving your current scope and role</small>
                                    </span>
                                </div>
                            </div>
                        ) : currentMembership ? (
                            <div className={styles.organizationSection}>
                                <span className={styles.sectionLabel}>Current Organization</span>
                                <div className={styles.organizationIdentity}>
                                    <Building2 size={17} />
                                    <span>
                                        <strong>{currentMembership.organization.name}</strong>
                                        {explicitRoles.map(assignment => (
                                            <small key={assignment.id}>
                                                {assignment.scopeName} · {assignment.role}
                                            </small>
                                        ))}
                                    </span>
                                </div>
                                {organizationContext && organizationContext.memberships.length > 1 ? (
                                    <div className={styles.organizationSwitcher}>
                                        {organizationContext.memberships
                                            .filter(membership => membership.organization.id !== currentMembership.organization.id)
                                            .map(membership => (
                                                <button
                                                    key={membership.organization.id}
                                                    type="button"
                                                    disabled={switching}
                                                    onClick={() => void switchOrganization(membership.organization.id)}
                                                >
                                                    Switch to {membership.organization.name}
                                                </button>
                                            ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : organizationSelectionRequired ? (
                            <div className={styles.organizationSection}>
                                <span className={styles.sectionLabel}>Select Organization</span>
                                <div className={styles.organizationSwitcher}>
                                    {organizationContext.memberships.map(membership => (
                                        <button
                                            key={membership.organization.id}
                                            type="button"
                                            disabled={switching}
                                            onClick={() => void switchOrganization(membership.organization.id)}
                                        >
                                            Select {membership.organization.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : import.meta.env.DEV && company ? (
                            <div className={styles.organizationSection}>
                                <span className={styles.sectionLabel}>Current Organization</span>
                                <div className={styles.organizationIdentity}>
                                    <Building2 size={17} />
                                    <span>
                                        <strong>{company.name}</strong>
                                        <small>{companyRole === "admin" ? "Administrator" : "Member"}</small>
                                    </span>
                                </div>
                            </div>
                        ) : null}

                        <div className={styles.menuEmail}>
                            <CircleUserRound size={16} className={styles.menuIcon} />
                            <span className={styles.menuEmailText} title={userDisplayData.email}>
                                {userDisplayData.email}
                            </span>
                        </div>

                        {canAccessAdministration ? (
                            <button
                                className={`${styles.commandButton} glass-interactive glass-focusable`}
                                onClick={() => {
                                    navigate("/app/admin");
                                    setMenuVisible(false);
                                }}
                            >
                                <ShieldCheck size={16} className={styles.menuIcon} />
                                Organization administration
                            </button>
                        ) : !administrationRequiresOrgAdmin && import.meta.env.DEV && roleReady && companyRole === "admin" ? (
                            <button
                                className={`${styles.commandButton} glass-interactive glass-focusable`}
                                onClick={() => {
                                    navigate("/app/admin-preview");
                                    setMenuVisible(false);
                                }}
                            >
                                <ShieldCheck size={16} className={styles.menuIcon} />
                                Organization administration preview
                            </button>
                        ) : null}

                        <button
                            className={`${styles.commandButton} glass-interactive glass-focusable`}
                            onClick={() => {
                                setIsSettingsOpen(true);
                                setMenuVisible(false);
                            }}
                        >
                            <Settings size={16} className={styles.menuIcon} />
                            {t("settings")}
                        </button>

                        {showLanguagePicker && (
                            <div className={styles.languagePickerWrapper}>
                                <button
                                    ref={languageButtonRef}
                                    className={`${styles.commandButton} glass-interactive glass-focusable`}
                                    onClick={() => setLanguageMenuOpen(v => !v)}
                                >
                                    <Languages size={16} className={styles.menuIcon} />
                                    {t("language")}
                                </button>

                                {languageMenuOpen && (
                                    <div ref={languageDropdownRef} className={`${styles.languageDropdown} glass-blur-fallback`}>
                                        {Object.entries(supportedLngs).map(([code, details]) => (
                                            <div
                                                key={code}
                                                className={`${styles.languageOption} ${code === i18n.language ? styles.activeLanguage : ""}`}
                                                onClick={() => {
                                                    i18n.changeLanguage(code);
                                                    setLanguageMenuOpen(false);
                                                }}
                                            >
                                                {details.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={styles.separator} />

                        <button
                            className={`${styles.commandButton} glass-interactive glass-focusable`}
                            aria-label={`Logout ${userDisplayData.name}`}
                            onClick={handleLogoutPopup}
                        >
                            <LogOut size={16} className={styles.menuIcon} />
                            {t("logout")}
                        </button>
                    </div>,
                    document.getElementById("dropdown-root")!
                )}

            {!loggedIn && !isLoading && (
                <DefaultButton onClick={handleLoginPopup} className={styles.loginButton} aria-label={"Login"}>
                    {t("login")}
                </DefaultButton>
            )}

            {isLoading && !loggedIn && <Spinner style={{ marginTop: "10px" }} />}

            {/* User Settings Modal */}
            <UserSettings isOpen={isSettingsOpen} onDismiss={() => setIsSettingsOpen(false)} />
        </div>
    );
};
